import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { Styles } from "./styles/sidebar.js";
import { useAuth } from "../../context/AuthContext";
import { AUTH_AVATAR_ENDPOINT, API_BASE_URL } from "../../config/api";

export const openSidebar = () => {
  const sidebarOverlay = document.getElementById("sidebar-overlay");
  const sidebarBody = document.getElementById("sidebar-body");
  if (sidebarBody?.classList.contains("pinned")) {
    sidebarBody.classList.add("opened");
    if (sidebarOverlay) {
      sidebarOverlay.classList.remove("visible");
    }
    return;
  }
  if (sidebarOverlay && sidebarBody) {
    sidebarOverlay.classList.add("visible");
    if (sidebarBody) {
      sidebarBody.classList.add("opened");
    }
  }
};

export const closeSidebar = () => {
  const sidebarOverlay = document.getElementById("sidebar-overlay");
  const sidebarBody = document.getElementById("sidebar-body");
  if (sidebarBody?.classList.contains("pinned")) {
    if (sidebarBody) {
      sidebarBody.classList.add("opened");
    }
    if (sidebarOverlay) {
      sidebarOverlay.classList.remove("visible");
    }
    return;
  }
  if (sidebarOverlay && sidebarBody) {
    sidebarOverlay.classList.remove("visible");
    sidebarBody.classList.remove("opened");
  }
};

const Sidebar = () => {
  const { user, logout, refreshUser } = useAuth();
  const location = useLocation();
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState("");
  const avatarInputRef = useRef(null);
  const history = useHistory();

  const getUserName = (account) => {
    if (!account) {
      return "";
    }
    const { firstName, lastName, username, email } = account;
    if (firstName || lastName) {
      return `${firstName || ""} ${lastName || ""}`.trim();
    }
    if (username) {
      return username;
    }
    if (email) {
      return email.split("@")[0];
    }
    return "User";
  };

  const getUserInitials = (account) => {
    const name = getUserName(account);
    if (!name) {
      return "U";
    }
    const parts = name.split(" ").filter(Boolean);
    if (parts.length === 0) {
      return name.charAt(0).toUpperCase();
    }
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(
      0
    )}`.toUpperCase();
  };

  const getUserAvatar = (account) => {
    if (!account) {
      return "";
    }
    const avatarFields = [
      account.avatarUrl,
      account.avatar,
      account.profileImage,
      account.profilePicture,
      account.photoURL,
      account.photoUrl,
    ];
    const avatar = avatarFields.find(
      (value) => typeof value === "string" && value.trim()
    );
    if (!avatar) {
      return "";
    }
    const trimmed = avatar.trim();
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }
    if (trimmed.startsWith("//")) {
      return `https:${trimmed}`;
    }
    const normalized = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    return `${API_BASE_URL}${normalized}`;
  };

  useEffect(() => {
    const sidebarBtn = document.getElementById("sidebar-btn");
    const sidebarOverlay = document.getElementById("sidebar-overlay");
    const sidebarExit = document.getElementById("close-sidebar");

    const handleOpen = (e) => {
      e.preventDefault();
      openSidebar();
    };

    const handleClose = (e) => {
      e.preventDefault();
      closeSidebar();
    };

    if (sidebarBtn) {
      sidebarBtn.addEventListener("click", handleOpen);
    }
    if (sidebarOverlay) {
      sidebarOverlay.addEventListener("click", handleClose);
    }
    if (sidebarExit) {
      sidebarExit.addEventListener("click", handleClose);
    }

    return () => {
      if (sidebarBtn) {
        sidebarBtn.removeEventListener("click", handleOpen);
      }
      if (sidebarOverlay) {
        sidebarOverlay.removeEventListener("click", handleClose);
      }
      if (sidebarExit) {
        sidebarExit.removeEventListener("click", handleClose);
      }
    };
  }, []);

  useEffect(() => {
    const sidebarBody = document.getElementById("sidebar-body");
    const sidebarOverlay = document.getElementById("sidebar-overlay");
    const sidebarBtn = document.getElementById("sidebar-btn");
    const bodyElement = document.body;

    if (user) {
      if (sidebarBody) {
        sidebarBody.classList.add("opened", "pinned");
      }
      if (sidebarOverlay) {
        sidebarOverlay.classList.remove("visible");
        sidebarOverlay.style.display = "none";
      }
      if (sidebarBtn) {
        sidebarBtn.style.display = "none";
      }
      bodyElement.classList.add("sidebar-pinned");
    } else {
      if (sidebarBody) {
        sidebarBody.classList.remove("pinned");
        sidebarBody.classList.remove("opened");
      }
      if (sidebarOverlay) {
        sidebarOverlay.style.display = "";
      }
      if (sidebarBtn) {
        sidebarBtn.style.display = "";
      }
      bodyElement.classList.remove("sidebar-pinned");
    }

    return () => {
      bodyElement.classList.remove("sidebar-pinned");
    };
  }, [user]);

  useEffect(() => {
    if (!user) {
      setAvatarError("");
      setUploadingAvatar(false);
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    closeSidebar();
  };

  const userNavItems = [
    { label: "Update Profile", to: "/profile" },
    { label: "My Applications", to: "/applications" },
    { label: "Search Course", to: "/courses" },
    { label: "Logout", action: handleLogout },
  ];

  const adminNavItems = [
    { label: "Dashboard", to: "/admin/dashboard" },
    { label: "Applications", to: "/admin/applications" },
    { label: "Users", to: "/admin/users" },
    { label: "Courses", to: "/admin/courses" },
    { label: "Universities", to: "/admin/universities" },
    { label: "Intakes", to: "/admin/intakes" },
    { label: "Status Types", to: "/admin/statustypes" },
    { label: "Agents", to: "/admin/agents" },
    { label: "Blogs", to: "/admin/blogs" },
    { label: "Events", to: "/admin/events" },
    { label: "Instructors", to: "/admin/instructors" },
    { label: "Gallery", to: "/admin/gallery" },
    { label: "Testimonials", to: "/admin/testimonials" },
    { label: "Logout", action: handleLogout },
  ];

  const normalizedRoles = Array.isArray(user?.roles)
    ? user.roles.map((role) =>
        typeof role === "string" ? role.toLowerCase() : role
      )
    : [];
  const isAdmin = normalizedRoles.includes("admin");

  const isActivePath = (targetPath) => {
    if (!targetPath) {
      return false;
    }
    const publicUrl = process.env.PUBLIC_URL || "";
    const normalizedTarget = `${publicUrl}${targetPath}`.replace(/\/+$/, "");
    const normalizedCurrent = location.pathname.replace(/\/+$/, "");
    if (normalizedCurrent === normalizedTarget) {
      return true;
    }
    return normalizedCurrent.startsWith(`${normalizedTarget}/`);
  };

  const handleAvatarClick = () => {
    if (!user || uploadingAvatar) {
      return;
    }
    setAvatarError("");
    if (avatarInputRef.current) {
      avatarInputRef.current.click();
    }
  };

  const handleAvatarFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setAvatarError("Please select an image file.");
      event.target.value = "";
      return;
    }

    if (file.size > 4 * 1024 * 1024) {
      setAvatarError("Image must be smaller than 4MB.");
      event.target.value = "";
      return;
    }

    setUploadingAvatar(true);
    setAvatarError("");

    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () =>
          reject(new Error("Unable to read selected file."));
        reader.readAsDataURL(file);
      });

      if (typeof dataUrl !== "string") {
        throw new Error("Unable to read selected file.");
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const response = await fetch(AUTH_AVATAR_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ dataUrl }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.message || "Unable to update profile image.");
      }

      if (payload.user) {
        localStorage.setItem("authUser", JSON.stringify(payload.user));
      }

      await refreshUser();
    } catch (error) {
      setAvatarError(error.message || "Unable to update profile image.");
    } finally {
      setUploadingAvatar(false);
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  return (
    <Styles data-pinned={user ? "true" : "false"}>
      {/* Sidebar */}
      {!user && (
        <a
          href={process.env.PUBLIC_URL + "/"}
          className="nav-link nav-sidebar"
          id="sidebar-btn"
          onClick={(e) => {
            e.preventDefault();
            openSidebar();
          }}
        >
          <i className="las la-bars"></i>
        </a>
      )}

      <div className="sidebar" id="sidebar-body">
        <div className="side-header">
          {user ? (
            <div className="profile">
              <div
                className={`avatar${uploadingAvatar ? " uploading" : ""}`}
                onClick={handleAvatarClick}
                role="button"
                tabIndex={0}
                aria-label="Update profile picture"
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleAvatarClick();
                  }
                }}
                title="Click to change profile picture"
              >
                {getUserAvatar(user) ? (
                  <img src={getUserAvatar(user)} alt={getUserName(user)} />
                ) : (
                  getUserInitials(user)
                )}
                {uploadingAvatar && (
                  <span className="uploading-indicator">Uploading…</span>
                )}
              </div>
              <div className="meta">
                <p className="name">{getUserName(user)}</p>
                <p className="role">{isAdmin ? "Administrator" : "Member"}</p>
              </div>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                className="avatar-input"
                onChange={handleAvatarFileChange}
                aria-hidden="true"
              />
              {avatarError && (
                <p className="avatar-error" role="alert">
                  {avatarError}
                </p>
              )}
            </div>
          ) : (
            <div className="brand">
              <Link to={process.env.PUBLIC_URL + "/"}>
                <img
                  src={process.env.PUBLIC_URL + "/assets/images/logo/logo2.png"}
                  alt="Swiss School"
                />
              </Link>
            </div>
          )}
          <button
            type="button"
            id="close-sidebar"
            className="close-btn"
            aria-label="Close sidebar"
          >
            <i className="las la-times"></i>
          </button>
        </div>
        {user ? (
          <div className="side-nav">
            {!isAdmin && (
              <>
                <h5>My Account</h5>
                <ul className="list-unstyled">
                  {userNavItems.map((item) => (
                    <li key={item.label}>
                      {item.action ? (
                        <Link
                          to="#"
                          className="nav-link-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            item.action();
                            closeSidebar();
                            history.push("/");
                          }}
                        >
                          {item.label}
                        </Link>
                      ) : (
                        <Link
                          to={process.env.PUBLIC_URL + item.to}
                          className={`nav-link-btn${
                            isActivePath(item.to) ? " active" : ""
                          }`}
                          onClick={closeSidebar}
                        >
                          {item.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {isAdmin && (
              <>
                <h5 className="nav-heading">Admin</h5>
                <ul className="list-unstyled">
                  {(isAdmin ? adminNavItems : userNavItems).map((item) => (
                    <li key={item.label}>
                      <Link
                        to={process.env.PUBLIC_URL + item.to}
                        className={`nav-link-btn${
                          isActivePath(item.to) ? " active" : ""
                        }`}
                        onClick={(e) => {
                          if (item.label === "Logout") {
                            e.preventDefault();
                            logout();
                            closeSidebar();
                            history.push("/");
                          } else {
                            closeSidebar();
                          }
                        }}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        ) : (
          <div className="side-nav">
            <h5>Welcome</h5>
            <p>Sign in to manage your profile and track applications.</p>
            <ul className="list-unstyled">
              <li>
                <Link
                  to={process.env.PUBLIC_URL + "/login"}
                  className="nav-link-btn"
                  onClick={closeSidebar}
                >
                  Log In
                </Link>
              </li>
              <li>
                <Link
                  to={process.env.PUBLIC_URL + "/registration"}
                  className="nav-link-btn"
                  onClick={closeSidebar}
                >
                  Register
                </Link>
              </li>
              <li>
                <Link
                  to={process.env.PUBLIC_URL + "/courses"}
                  className="nav-link-btn"
                  onClick={closeSidebar}
                >
                  Search Course
                </Link>
              </li>
            </ul>
          </div>
        )}
      </div>
      <div className="sidebar-overlay" id="sidebar-overlay"></div>
    </Styles>
  );
};

export default Sidebar;
